import { isUndefined } from 'lodash-es';
import { useNavigate, useParams } from 'react-router';
import { match } from 'ts-pattern';

import { APPLICATION_ROUTES } from '~/core/application/routing/routes';
import { useEventHandler } from '../../../../hooks/useEventHandler';
import { WorkflowSession } from '../../types/WorkflowSession';
import { useRedirectToWorkflowCurrentPlace } from './hooks/useRedirectToWorkflowCurrentPlace';
import { useWorkflowRootDocument } from './hooks/useWorkflowRootDocument';

export const useWorkflowSessionManagement = (): WorkflowSession => {
  const navigate = useNavigate();
  const {
    document: documentId = '',
    workflow: workflowName = '',
    place,
  } = useParams<{ document?: string; workflow?: string; place?: string }>();

  const { rootDocument, refetch, isSuccess, isFetching, meta } =
    useWorkflowRootDocument(documentId, workflowName, place);

  useRedirectToWorkflowCurrentPlace(
    rootDocument,
    workflowName,
    place,
    meta?.currentPlace,
  );

  const reset = useEventHandler(
    (exitReason: 'session-expired' | 'unknown' | 'error' = 'unknown'): void => {
      console.log(`Resetting workflow controller, reason: ${exitReason}`);
      const url = match(exitReason)
        .with('session-expired', () => APPLICATION_ROUTES.sessionExpired.path)
        .with('error', () => APPLICATION_ROUTES.error.path)
        .otherwise(() => '/workflow/exited'); // TODO: @haxxxton - this should come from tenant config
      navigate(url);
    },
  );

  const rehydrateRootDocument = useEventHandler(async (): Promise<void> => {
    await refetch();
  });

  return {
    rootDocument,
    documentId,
    workflowName,
    place,
    targetPlace: meta?.currentPlace,
    hasInitialised: isSuccess && !isUndefined(rootDocument),
    isLoading: isFetching,

    reset,
    rehydrateRootDocument,
  };
};
